/**
 * Permisos del panel alineados con backend/business_access.py (validación real en API).
 */
import {
  DASHBOARD_ROUTE_PERMISSION_RULES,
  dashboardRegistryPathsMissingPermission,
} from '@/soluqr/tiers/dashboardRoutePermissions';

export { dashboardRegistryPathsMissingPermission };

export const PERM = {
  DASHBOARD: 'dashboard',
  CONFIG: 'config',
  PRODUCTS: 'products',
  PROMOS: 'promos',
  BENEFITS: 'benefits',
  CUSTOMERS: 'customers',
  ORDERS: 'orders',
  ORDERS_LIVE: 'orders_live',
  KDS: 'kds',
  POS: 'pos',
  TABLES: 'tables',
  ANALYTICS: 'analytics',
  BROADCAST: 'broadcast',
  NOTIFICATIONS: 'notifications',
  QR: 'qr',
  MEMBERSHIP: 'membership',
  PAYMENTS: 'payments',
  SUPPORT: 'support',
  ASSISTANT_TRAINING: 'assistant_training',
  TEAM_MANAGE: 'team_manage',
};

/** Coincide con STORED_ROLES + owner en backend; cualquier otro valor cae en `unknown`. */
export const KNOWN_PANEL_ROLES = Object.freeze([
  'owner',
  'admin',
  'caja',
  'cocina',
  'mesero',
]);

export const ROLE_UNKNOWN = 'unknown';

const ALL = new Set(Object.values(PERM));

const ROLE_PERMS = {
  owner: ALL,
  admin: new Set([...ALL].filter((p) => p !== PERM.TEAM_MANAGE)),
  caja: new Set([
    PERM.DASHBOARD,
    PERM.ORDERS,
    PERM.ORDERS_LIVE,
    PERM.POS,
    PERM.PAYMENTS,
    PERM.SUPPORT,
  ]),
  cocina: new Set([PERM.DASHBOARD, PERM.ORDERS, PERM.KDS, PERM.SUPPORT]),
  /** Solo toma de pedidos, mesas y pedidos en vivo (sin Dashboard / SoluGo / QR / caja). */
  mesero: new Set([PERM.ORDERS, PERM.ORDERS_LIVE, PERM.TABLES]),
  [ROLE_UNKNOWN]: new Set(),
};

const ROUTE_RULES = [
  ...DASHBOARD_ROUTE_PERMISSION_RULES,
  // Alias legacy (no están en registry)
  ['/dashboard/cashier', PERM.POS],
  ['/dashboard/live-orders', PERM.ORDERS_LIVE],
];

export function normalizeDashboardRole(raw) {
  const r = String(raw || '').trim().toLowerCase();
  if (!r) return ROLE_UNKNOWN;
  const known = new Set(KNOWN_PANEL_ROLES);
  return known.has(r) ? r : ROLE_UNKNOWN;
}

/**
 * Regla del fallback AuthContext: primero rol desde GET /business/config;
 * si sigue unknown, usar el de GET /business/dashboard-role (misma normalización).
 */
export function mergeDashboardRoleSources(configDashboardRoleRaw, fallbackDashboardRoleRaw) {
  const fromConfig = normalizeDashboardRole(configDashboardRoleRaw);
  if (fromConfig !== ROLE_UNKNOWN) return fromConfig;
  return normalizeDashboardRole(fallbackDashboardRoleRaw);
}

export function roleHasPermission(role, permission) {
  const r = normalizeDashboardRole(role);
  const set = ROLE_PERMS[r];
  if (!set) return false;
  return set.has(permission);
}

/** Alias explícito: ¿el rol permite un permiso concreto? */
export function canAccess(role, permission) {
  return roleHasPermission(role, permission);
}

/** Invité de cocina: solo KDS / pedidos de preparación, sin tomar pedidos ni SoluGo. */
export function roleIsKitchenStaff(role) {
  return normalizeDashboardRole(role) === 'cocina';
}

/** Invité mesero: tomar pedidos, mesas y en vivo (sin Dashboard / SoluGo / QR). */
export function roleIsWaiterStaff(role) {
  return normalizeDashboardRole(role) === 'mesero';
}

/**
 * Rutas de UI permitidas al rol cocina (el resto del panel no debe aparecer en el menú).
 * El API de pedidos sigue usando PERM.ORDERS en backend para el KDS.
 */
export const KITCHEN_STAFF_NAV_PATHS = Object.freeze([
  '/dashboard/kds',
  '/dashboard/support',
  '/dashboard/orders/kitchen',
]);

/** Rutas de UI permitidas al rol mesero. */
export const WAITER_STAFF_NAV_PATHS = Object.freeze([
  '/dashboard/orders',
  '/dashboard/tables',
  '/dashboard/orders-live',
  '/dashboard/live-orders',
]);

export function isKitchenStaffAllowedPath(pathname) {
  const p = String(pathname || '')
    .split('?')[0]
    .replace(/\/$/, '') || '/dashboard';
  return KITCHEN_STAFF_NAV_PATHS.some(
    (allowed) => p === allowed || p.startsWith(`${allowed}/`),
  );
}

export function isWaiterStaffAllowedPath(pathname) {
  const p = String(pathname || '')
    .split('?')[0]
    .replace(/\/$/, '') || '/dashboard';
  return WAITER_STAFF_NAV_PATHS.some(
    (allowed) => p === allowed || p.startsWith(`${allowed}/`),
  );
}

/** Tomar pedido (WhatsApp/texto → registro). No para cocina. */
export function roleCanTakeOrders(role) {
  return !roleIsKitchenStaff(role) && roleHasPermission(role, PERM.ORDERS);
}

/** Gestionar / solicitar SoluGo. No para cocina ni mesero. */
export function roleCanManageSoluGo(role) {
  return (
    !roleIsKitchenStaff(role) &&
    !roleIsWaiterStaff(role) &&
    roleHasPermission(role, PERM.ORDERS)
  );
}

export function permissionForPath(pathname) {
  const p = pathname.split('?')[0].replace(/\/$/, '') || '/dashboard';
  for (const [prefix, perm] of ROUTE_RULES) {
    const pref = prefix.replace(/\/$/, '');
    if (p === pref || p.startsWith(`${pref}/`)) return perm;
  }
  return PERM.DASHBOARD;
}

export function canAccessPath(role, pathname) {
  if (normalizeDashboardRole(role) === ROLE_UNKNOWN) return false;
  // Cocina: menú mínimo (KDS + soporte). No Dashboard / Pedidos / SoluGo.
  if (roleIsKitchenStaff(role)) {
    return isKitchenStaffAllowedPath(pathname);
  }
  // Mesero: solo Pedidos (tomar), Mesas y Pedidos en vivo.
  if (roleIsWaiterStaff(role)) {
    return isWaiterStaffAllowedPath(pathname);
  }
  return roleHasPermission(role, permissionForPath(pathname));
}

export function filterNavItemsForRole(items, role) {
  if (roleIsKitchenStaff(role)) {
    return (items || []).filter((item) => {
      if (!item?.path) return false;
      const p = String(item.path).replace(/\/$/, '');
      // Sidebar: solo KDS y Soporte (no pantalla TV ni rutas internas).
      return p === '/dashboard/kds' || p === '/dashboard/support';
    });
  }
  if (roleIsWaiterStaff(role)) {
    return (items || [])
      .filter((item) => {
        if (!item?.path) return false;
        const p = String(item.path).replace(/\/$/, '');
        return (
          p === '/dashboard/orders' ||
          p === '/dashboard/tables' ||
          p === '/dashboard/orders-live'
        );
      })
      .map((item) => {
        const p = String(item.path).replace(/\/$/, '');
        if (p === '/dashboard/orders') {
          return { ...item, label: 'Tomar pedido' };
        }
        return item;
      });
  }
  return (items || []).filter((item) => {
    if (!item?.path) return true;
    return canAccessPath(role, item.path);
  });
}

/** Primera ruta permitida (tras login o tras “Ir al inicio” sin permiso en la URL actual). */
export function defaultHomePathForRole(role) {
  const r = normalizeDashboardRole(role);
  if (r === ROLE_UNKNOWN) return '/dashboard';
  if (r === 'cocina') return '/dashboard/kds';
  if (r === 'caja') {
    if (roleHasPermission(r, PERM.POS)) return '/dashboard/cashier';
    if (roleHasPermission(r, PERM.ORDERS)) return '/dashboard/orders';
    return '/dashboard';
  }
  if (r === 'mesero') {
    if (roleHasPermission(r, PERM.TABLES)) return '/dashboard/tables';
    if (roleHasPermission(r, PERM.ORDERS_LIVE))
      return '/dashboard/orders-live';
    if (roleHasPermission(r, PERM.ORDERS)) return '/dashboard/orders';
    return '/dashboard';
  }
  if (r === 'owner' || r === 'admin') return '/dashboard';

  const order = [
    '/dashboard',
    '/dashboard/pos',
    '/dashboard/orders-live',
    '/dashboard/kds',
    '/dashboard/orders',
  ];
  for (const path of order) {
    if (canAccessPath(role, path)) return path;
  }
  return '/dashboard';
}

/** ¿Se pudo resolver un rol de panel válido (no anonymous / corrupto)? */
export function roleIsResolved(role) {
  const r = normalizeDashboardRole(role);
  return KNOWN_PANEL_ROLES.includes(r);
}
